import { useTolgee } from '@tolgee/react';
import { useState } from 'react';
import {
  IProductLangInfo,
  IProductNames,
  SpecialProduct,
  SupportedLanguage,
} from '../../interfaces/ISpecialOrder';

export interface productInCart extends SpecialProduct {
  cartQuantity: number;
  quantityCost: number;
}

const useSpecialOrder = () => {
  const { getLanguage } = useTolgee();
  const language = getLanguage() as SupportedLanguage;
  const [cartItems, setCartItems] = useState<Map<number, productInCart>>(
    new Map(),
  );
  const [existingCart, setExistingCart] = useState<Map<number, productInCart>>(
    new Map(),
  );
  const [specialOrderId, setSpecialOrderId] = useState<number | null>(null);
  const [specialOrderComments, setSpecialOrderComments] = useState<string>('');
  const [existingComment, setExistingComments] = useState<string>('');

  const handleProductName = (
    productNames: IProductNames,
    language: SupportedLanguage,
  ): IProductLangInfo => {
    return productNames[language] || productNames.en;
  };

  const [deliveryCharges, setDeliveryCharges] = useState<{
    standardDeliveryCharge: number;
    previousDeliveryCharge: number;
    repeatingOrderDeliveryCharge: number;
    subscriptionProductIds: number[];
  }>({
    standardDeliveryCharge: 0,
    previousDeliveryCharge: 0,
    repeatingOrderDeliveryCharge: 0,
    subscriptionProductIds: [],
  });

  const [specialOrderRules, setSpecialOrderRules] = useState<
    {
      productType: string;
      isFruit: number;
      minQuantity: number;
    }[]
  >([]);

  const [cutoffHours, setCutoffHours] = useState<number>(72);

  const handleSetExistingComment = (str: string) => {
    setSpecialOrderComments(str);
    setExistingComments(str);
  };

  const addToCart = (item: SpecialProduct) => {
    setCartItems(prevCartItems => {
      const updatedCart = new Map(prevCartItems);
      if (updatedCart.has(item.productId)) {
        const existingItem = updatedCart.get(item.productId)!;
        existingItem.cartQuantity = (existingItem.cartQuantity || 0) + 1;
        existingItem.quantityCost =
          existingItem.cartQuantity * existingItem.price;
      } else {
        updatedCart.set(item.productId, {
          ...item,
          cartQuantity: 1,
          quantityCost: item.price,
        });
      }

      return updatedCart;
    });
  };
  const setExistingOrderOnDay = (data: productInCart[], orderId = 0) => {
    setCartItems(() => {
      const existingCart = new Map();
      let specialOrderId = null;
      if (orderId) {
        specialOrderId = orderId;
        data.forEach(item => {
          if (item.cartQuantity > 0) {
            existingCart.set(item.productId, {
              ...item,
              specialOrderQuantity: item.cartQuantity,
              quantityCost: item.cartQuantity * item.price,
            });
          }
        });
      } else {
        data.forEach(item => {
          if (item.specialOrderQuantity > 0) {
            specialOrderId = item.specialOrderId;
            existingCart.set(item.productId, {
              ...item,
              cartQuantity: item.specialOrderQuantity,
              quantityCost: item.specialOrderQuantity * item.price,
            });
          }
        });
      }
      setSpecialOrderId(specialOrderId);
      setExistingCart(existingCart);
      return existingCart;
    });
  };
  const removeFromCart = (productId: number) => {
    setCartItems(prevCartItems => {
      const updatedCart = new Map(prevCartItems);
      if (updatedCart.has(productId)) {
        const existingItem = updatedCart.get(productId)!;
        if (
          existingItem.cartQuantity &&
          (existingItem.cartQuantity > 1 || existingCart.get(productId))
        ) {
          existingItem.cartQuantity -= 1;
          existingItem.quantityCost =
            existingItem.cartQuantity * existingItem.price;
        } else {
          updatedCart.delete(productId);
        }
      } else {
        console.log(`Item with ID ${productId} is not in the cart.`);
      }

      return updatedCart;
    });
  };

  const clearCart = () => {
    setCartItems(new Map());
    setSpecialOrderComments('');
  };

  const state = {
    cartItems,
    existingCart,
    itemsInCart: Array.from(cartItems.values()).reduce(
      (total, item) => total + (item.cartQuantity || 0),
      0,
    ),
    totalCartCost: Array.from(cartItems.values()).reduce(
      (total, item) => total + (item.quantityCost || 0),
      0,
    ),
    specialOrderId,
    isExistingCartUpdated:
      !areCartsEqual(cartItems, existingCart) ||
      existingComment != specialOrderComments,
    existingCartSize: existingCart.size,
    specialOrderComments,
    deliveryCharges,
    specialOrderRules,
    cutoffHours,
  };

  const actions = {
    addToCart,
    removeFromCart,
    clearCart,
    setExistingOrderOnDay,
    handleSetExistingComment,
    setExistingCart,
    setSpecialOrderId,
    setSpecialOrderComments,
    setDeliveryCharges,
    handleProductName,
    setSpecialOrderRules,
    setCutoffHours,
  };
  return { state, actions };
};
const areCartsEqual = (
  cart1: Map<number, productInCart>,
  cart2: Map<number, productInCart>,
): boolean => {
  if (cart1.size !== cart2.size) {
    return false;
  }
  for (const [productId, product] of cart1.entries()) {
    const matchingProduct = cart2.get(productId);
    if (
      !matchingProduct ||
      product.cartQuantity !== matchingProduct.specialOrderQuantity
    ) {
      return false;
    }
  }

  return true;
};
export default useSpecialOrder;
